import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../../i8n";
import Modal from "../Modal/Modal";
import Badge from "../Badge/Badge";
import { CiCircleCheck, CiClock1 } from "react-icons/ci";
import { CgDanger } from "react-icons/cg";
import { getApiInstance } from "../../../utils/api";
import { formatDate } from "../../../utils/utils";
import "./ValidacionesHistorico.scss";

/* ValidationState del backend (wlc_back/Utils/Constantes.cs).
   Ojo: la consulta del histórico no devuelve CodEstado, el estado viene en `valor`. */
/* El 4 es NODOCUMENT en el enum, pero las fichas de gestor y de centro productor
   ofrecen "Expirado" con ese valor en su selector de estado: aquí se etiqueta como
   lo que el usuario eligió, no como lo que dice el enum. */
/* label es un getter para que salga en el idioma activo. */
const estado = (type, clave, Icon) => ({
    type,
    Icon,
    get label() {
        return i18n.t(`validations.${clave}`);
    },
});
const ESTADOS = {
    1: estado("info", "pending", CiClock1),
    2: estado("warning", "withIncidents", CgDanger),
    3: estado("success", "validated", CiCircleCheck),
    4: estado("danger", "expired", CgDanger),
    5: estado("danger", "expired", CgDanger),
    6: estado("success", "closed", CiCircleCheck),
};

export const badgeValidacion = (estado) =>
    ESTADOS[Number(estado)] ?? ESTADOS[1];

/**
 * Histórico de validaciones de un elemento, tal y como lo mostraba la aplicación
 * antigua (`seeValidacion` en Views/Obras/Details.cshtml): estado, fecha y
 * observaciones de cada validación, de la más reciente a la más antigua.
 *
 * Se pide al abrir, no al montar la ficha: así se ven las validaciones que el
 * usuario acaba de hacer sin recargar la página.
 */
export default function ValidacionesHistorico({
    isOpen,
    onClose,
    codTipoValidacion,
    codClave,
    title,
}) {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen || codClave == null || codTipoValidacion == null) return;

        let vigente = true;
        setLoading(true);

        const api = getApiInstance();
        api.get(
            `/api/Validaciones/GetValidacionesByCodClave/${codTipoValidacion}/${codClave}`
        )
            .then(({ data }) => {
                if (vigente) setItems(Array.isArray(data) ? data : []);
            })
            .catch((error) => {
                /* Un 404 aquí solo significa "sin validaciones todavía". */
                if (error?.response?.status !== 404) {
                    console.error(
                        "Error cargando el histórico de validaciones:",
                        error
                    );
                }
                if (vigente) setItems([]);
            })
            .finally(() => {
                if (vigente) setLoading(false);
            });

        return () => {
            vigente = false;
        };
    }, [isOpen, codTipoValidacion, codClave]);

    /* La más reciente primero: es el orden en el que se lee un histórico y el
       mismo criterio con el que el backend calcula el último estado. */
    const ordenados = [...items].sort(
        (a, b) => (b.codValidacion ?? 0) - (a.codValidacion ?? 0)
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            position="center"
            title={title ?? t("validations.historyTitle")}
            actions={
                <button
                    className="validaciones-historico__close-btn"
                    onClick={onClose}
                >
                    {t("common.close")}
                </button>
            }
        >
            <div className="validaciones-historico">
                {loading ? (
                    <div className="validaciones-historico__loading">
                        {t("validations.loading")}
                    </div>
                ) : ordenados.length === 0 ? (
                    <div className="validaciones-historico__empty">
                        {t("validations.none")}
                    </div>
                ) : (
                    ordenados.map((validacion, index) => {
                        const badge = badgeValidacion(
                            validacion.valor ?? validacion.codEstado
                        );

                        return (
                            <div
                                key={validacion.codValidacion ?? index}
                                className="validaciones-historico__item"
                            >
                                <div className="validaciones-historico__item-header">
                                    <Badge
                                        variant={badge.type}
                                        Icon={badge.Icon}
                                    >
                                        {badge.label}
                                    </Badge>
                                    <span className="validaciones-historico__item-fecha">
                                        {formatDate(
                                            validacion.fecha ??
                                                validacion.fechaValidacion
                                        )}
                                    </span>
                                </div>
                                <div className="validaciones-historico__item-obs">
                                    {validacion.observaciones ||
                                        "Sin observaciones"}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </Modal>
    );
}
